import { useTranslation } from 'react-i18next';
import { PageSubtitle } from '@components/ui/PageFrame/PageFrame';
import { RefreshIcon } from '@utils/icons';
import { useNow } from '@hooks/useNow';
import { formatAgo } from '@utils/dateUtils';
import styles from './styles.module.css';

interface IPortfolioSyncProps {
  syncedAtIso: string;
  onRefresh: () => void;
  isRefreshing: boolean;
}

/**
 * The portfolio's subtitle once it has data: the route's line with "last
 * synced" and the refresh control after it. Absent while loading or on an
 * error, when there is nothing to sync and the route's own subtitle stands.
 */
export function PortfolioSync({ syncedAtIso, onRefresh, isRefreshing }: IPortfolioSyncProps) {
  const { t, i18n } = useTranslation();
  // Tick every 30s so the relative "last synced" label stays current
  // ("just now" → "2 minutes ago"). Local clock only — no network.
  const now = useNow(30_000);
  const ago = formatAgo(syncedAtIso, now, i18n.language);
  return (
    <PageSubtitle>
      <span>{t('portfolio.subtitle')}</span>
      <span className={styles.dot} aria-hidden="true">·</span>
      <span className={styles.synced}>{t('portfolio.lastSynced', { ago })}</span>
      <button
        type="button"
        className={`${styles.refresh}${isRefreshing ? ` ${styles.spinning}` : ''}`}
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label={t('portfolio.refresh')}
        title={t('portfolio.refresh')}
      >
        <RefreshIcon size={14} />
      </button>
    </PageSubtitle>
  );
}
